import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { fmtDate, fmtRelative } from "../../format.ts";
import { Badge, Button, CopyButton, Icon, Mono, cn, type IconName } from "../../ui/index.tsx";
import { LogoMark } from "../../ui/logo.tsx";
import { Prose, type LoaderLatest } from "../_shared.tsx";
import { Aurora, EASE, rise, stagger, useCalm } from "./kit.tsx";

const DOWNLOAD_URL = "/api/v2/loader/download";

/** Primary call to action: halo, shimmer sweep, bobbing arrow, and a confirmation state after the click. */
function DownloadButton({ version }: { version: string }) {
  const calm = useCalm();
  const [started, setStarted] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className="relative inline-block">
      {!calm && (
        <motion.span className="absolute -inset-1 rounded-2xl bg-accent/50 blur-xl" aria-hidden
          animate={{ opacity: [0.35, 0.75, 0.35], scale: [1, 1.06, 1] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }} />
      )}
      <motion.a href={DOWNLOAD_URL}
        onClick={() => { setStarted(true); clearTimeout(timer.current); timer.current = setTimeout(() => setStarted(false), 4500); }}
        whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 420, damping: 18 }}
        className="relative inline-flex h-16 items-center gap-4 overflow-hidden rounded-xl bg-gradient-to-b from-accent-hover to-accent px-8 text-accent-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.3),0_14px_36px_-10px_rgb(255_61_79/0.8)]">
        {!calm && (
          <motion.span className="absolute inset-y-0 w-1/3 -skew-x-20 bg-gradient-to-r from-transparent via-white/35 to-transparent" aria-hidden
            initial={{ x: "-150%" }} animate={{ x: "450%" }} transition={{ duration: 1.3, repeat: Infinity, repeatDelay: 2.2, ease: "easeInOut" }} />
        )}
        <span className="relative grid size-9 place-items-center rounded-lg bg-black/20">
          <AnimatePresence mode="wait" initial={false}>
            {started ? (
              <motion.span key="ok" initial={{ scale: 0, rotate: -60 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0 }} transition={{ type: "spring", stiffness: 420, damping: 16 }}>
                <Icon name="check" className="size-5" />
              </motion.span>
            ) : (
              <motion.span key="dl" exit={{ scale: 0 }} animate={calm ? undefined : { y: [-1.5, 2.5, -1.5] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>
                <Icon name="download" className="size-5" />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
        <span className="relative text-left leading-tight">
          <span className="block font-display text-lg font-semibold tracking-wide">{started ? "Download started" : "Download loader"}</span>
          <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-white/75">v{version} · Windows x64</span>
        </span>
      </motion.a>
    </div>
  );
}

/** Logo core with slow orbiting rings and three satellites (key, shield, game). */
function OrbitVisual() {
  const calm = useCalm();
  const spin = (d: number, reverse?: boolean) => (calm ? {} : { animate: { rotate: reverse ? -360 : 360 }, transition: { duration: d, repeat: Infinity, ease: "linear" as const } });
  const sats: { icon: IconName; angle: number }[] = [{ icon: "key", angle: 0 }, { icon: "shield", angle: 120 }, { icon: "gamepad", angle: 240 }];
  return (
    <div className="relative hidden size-64 shrink-0 place-items-center md:grid" aria-hidden>
      <div className="absolute inset-6 rounded-full bg-accent/20 blur-3xl" />
      <motion.svg viewBox="0 0 100 100" className="absolute inset-0 size-full text-accent/40" {...spin(40)}>
        <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.4" strokeDasharray="2 3" />
      </motion.svg>
      <motion.svg viewBox="0 0 100 100" className="absolute inset-4 size-[calc(100%-2rem)] text-ember/40" {...spin(28, true)}>
        <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="14 6 2 6" />
      </motion.svg>
      <motion.div className="absolute inset-0" {...spin(24)}>
        {sats.map((s) => (
          <div key={s.icon} className="absolute left-1/2 top-1/2 size-0" style={{ transform: `rotate(${s.angle}deg) translateY(-7rem)` }}>
            <motion.div className="-ml-4 -mt-4 grid size-8 place-items-center rounded-lg border border-line-strong bg-surface text-accent shadow-lg" initial={{ rotate: -s.angle }}
              animate={calm ? undefined : { rotate: [-s.angle, -s.angle - 360] }} transition={{ duration: 24, repeat: Infinity, ease: "linear" }}>
              <Icon name={s.icon} className="size-4" />
            </motion.div>
          </div>
        ))}
      </motion.div>
      <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 160, damping: 14, delay: 0.2 }}>
        <LogoMark className="size-28 drop-shadow-[0_10px_30px_rgb(255_61_79/0.55)]" />
      </motion.div>
    </div>
  );
}

function Changelog({ text, version }: { text: string; version: string }) {
  const long = text.length > 320;
  const [open, setOpen] = useState(!long);
  return (
    <div className="relative border-t border-line bg-bg/30 px-6 py-5 sm:px-8">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="kicker">// changelog · v{version}</div>
        {long && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-muted transition-colors hover:text-fg" aria-expanded={open}>
            {open ? "Show less" : "Show more"}
          </button>
        )}
      </div>
      <motion.div initial={false} animate={{ height: open ? "auto" : 96 }} transition={{ duration: 0.4, ease: EASE }} className="relative overflow-hidden">
        <Prose text={text} />
        {!open && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-surface to-transparent" />}
      </motion.div>
    </div>
  );
}

/** Radar sweep shown until the first loader build is published. */
function Pending() {
  const calm = useCalm();
  return (
    <div className="relative flex flex-col items-center gap-8 px-6 py-12 text-center sm:px-8 md:flex-row md:text-left">
      <div className="relative grid size-52 shrink-0 place-items-center" aria-hidden>
        {[1, 0.7, 0.4].map((s) => <span key={s} className="absolute rounded-full border border-accent/25" style={{ width: `${s * 100}%`, height: `${s * 100}%` }} />)}
        <span className="absolute h-px w-full bg-accent/15" /><span className="absolute h-full w-px bg-accent/15" />
        <motion.span className="absolute inset-0 rounded-full" style={{ background: "conic-gradient(from 0deg, transparent 0 78%, rgb(255 61 79 / 0.45) 100%)" }}
          animate={calm ? undefined : { rotate: 360 }} transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }} />
        <LogoMark className="relative size-14 opacity-90" animated={false} />
        {!calm && [{ x: 52, y: -34, d: 0.6 }, { x: -44, y: 28, d: 1.9 }].map((b, i) => (
          <motion.span key={i} className="absolute size-1.5 rounded-full bg-ember" style={{ x: b.x, y: b.y }}
            animate={{ opacity: [0, 1, 0], scale: [0.6, 1.6, 0.6] }} transition={{ duration: 3.4, repeat: Infinity, delay: b.d }} />
        ))}
      </div>
      <div className="max-w-md">
        <div className="kicker text-warn">// build pending</div>
        <h2 className="mt-2 font-display text-2xl font-semibold">No loader build is published yet</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          The first build hasn't gone live. Your licenses are safe and will work the moment it lands, so check back soon.
          Need it sooner? <Link to="/support" className="text-accent hover:underline">Open a ticket</Link>.
        </p>
      </div>
    </div>
  );
}

function HeroSkeleton() {
  return (
    <div className="space-y-4 p-8" aria-busy>
      {[40, 70, 55].map((w, i) => <div key={i} className="h-5 animate-pulse-soft rounded bg-raised" style={{ width: `${w}%` }} />)}
      <div className="h-16 w-72 animate-pulse-soft rounded-xl bg-raised" />
    </div>
  );
}

export function LoaderHero({ loader, loading, error, retry }: { loader: LoaderLatest | undefined; loading: boolean; error: { message: string } | null; retry: () => void }) {
  return (
    <motion.section className="pane hud hud-accent relative overflow-hidden" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
      <Aurora />
      <div className="grid-floor pointer-events-none absolute inset-x-0 top-0 h-full opacity-40" aria-hidden />
      <div className="relative">
        {loading && !loader ? <HeroSkeleton /> : error && !loader ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <p className="text-sm text-bad">{error.message}</p>
            <Button size="sm" icon="refresh" onClick={retry}>Try again</Button>
          </div>
        ) : !loader?.version ? <Pending /> : (
          <>
            <div className="flex items-center justify-between gap-8 px-6 py-8 sm:px-8 sm:py-10">
              <motion.div className="min-w-0" variants={stagger(0.09, 0.15)} initial="hidden" animate="show">
                <motion.div variants={rise} className="kicker flex items-center gap-2 text-accent">
                  <span className="size-1.5 animate-pulse-soft rounded-full bg-accent shadow-[0_0_10px_var(--color-accent)]" />// loader.latest
                </motion.div>
                <motion.h1 variants={rise} className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                  ExoWare <span className="text-gradient">Loader</span>
                </motion.h1>
                <motion.div variants={rise} className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted">
                  <Badge tone="accent">v{loader.version}</Badge>
                  <Badge tone="ok" dot>Latest</Badge>
                  <span title={fmtDate(loader.created_at)}>Released {fmtRelative(loader.created_at)} · {fmtDate(loader.created_at)}</span>
                </motion.div>
                <motion.div variants={rise} className="mt-7"><DownloadButton version={loader.version} /></motion.div>
                {loader.sha256 && (
                  <motion.div variants={rise} className="mt-7 max-w-xl">
                    <div className="flex items-center gap-2 rounded-lg border border-line bg-bg/70 px-3 py-2 backdrop-blur">
                      <Icon name="shield" className="size-4 shrink-0 text-ok" />
                      <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] text-faint">SHA-256</span>
                      <Mono className="min-w-0 flex-1 truncate">{loader.sha256}</Mono>
                      <CopyButton text={loader.sha256} label="Copy SHA-256" />
                    </div>
                    <p className="mt-2 text-xs text-faint">
                      Check your download in Command Prompt with <Mono className={cn("rounded bg-raised px-1 py-0.5")}>certutil -hashfile &lt;file&gt; SHA256</Mono> and compare the result.
                    </p>
                  </motion.div>
                )}
              </motion.div>
              <OrbitVisual />
            </div>
            {loader.changelog && <Changelog text={loader.changelog} version={loader.version} />}
          </>
        )}
      </div>
    </motion.section>
  );
}
